import { useEffect, useState } from "react";
import { toast } from "sonner";
import usePackkySetting from "../../Sevices/usePackkySetting";

const clean = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== undefined && v !== null && `${v}`.trim() !== ""
    )
  );

export default function DriverOffersTab() {
  const {
    fetchOffersApi,
    createOfferApi,
    deleteOfferApi,
    addIncentiveApi,
  } = usePackkySetting();

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedOfferId, setExpandedOfferId] = useState(null);

  // trigger refetch
  const [refreshKey, setRefreshKey] = useState(0);

  // Create Offer
  const [title, setTitle] = useState("");
  const [totalEarnings, setTotalEarnings] = useState("");
  const [validity, setValidity] = useState("");

  // Incentive (per-offer)
  const [gigsLevel, setGigsLevel] = useState("");
  const [orders, setOrders] = useState("");
  const [earnings, setEarnings] = useState("");
  const [incentive, setIncentive] = useState("");

  // Fetch offers on mount + refresh
  useEffect(() => {
    fetchOffersApi(setOffers, setLoading);
  }, [refreshKey]);

  // ---------------- Insert Offer ----------------
  const handleCreateOffer = async (e) => {
    e.preventDefault();

    const body = clean({ title, totalEarnings, validity });

    await createOfferApi(body, setLoading, () => {
      setTitle("");
      setTotalEarnings("");
      setValidity("");
      setRefreshKey((k) => k + 1);
    });
  };

  // ---------------- Delete Offer ----------------
  const handleDeleteOffer = async (offer) => {
    const offerId =
      offer?.offerid ||
      offer?.offerId ||
      offer?.pk_offer_id ||
      offer?.id;

    if (!offerId) return toast.error("Offer ID missing");

    await deleteOfferApi(offerId, setLoading, () =>
      setRefreshKey((k) => k + 1)
    );
  };

  // ---------------- Add Incentive ----------------
  const handleAddIncentive = async (offer) => {
    const offerId =
      offer?.offerId ||
      offer?.offerid ||
      offer?.pk_offer_id ||
      offer?.id;

    if (!offerId) return toast.error("Offer ID missing");

    const body = clean({
      offerId: `${offerId}`,
      gigsLevel,
      orders,
      earnings,
      incentive,
    });

    await addIncentiveApi(body, setLoading, () => {
      setGigsLevel("");
      setOrders("");
      setEarnings("");
      setIncentive("");
      setExpandedOfferId(null);
      setRefreshKey((k) => k + 1);
    });
  };

  return (
   <div className="admin-driver-offers p-2 space-y-6">

  {/* ===== TOP BAR ===== */}
  <div className="admin-bar flex items-center gap-3">
    <button
      onClick={() => fetchOffersApi(setOffers, setLoading, true)}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 transition"
    >
      Refresh
    </button>
    {loading && <span className="text-xs text-gray-500">Loading…</span>}
  </div>

  {/* ===== CREATE OFFER CARD ===== */}
  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
    <div className="flex items-center justify-between px-4 py-3 border-b">
      <h3 className="text-sm font-semibold">Create Driver Offer</h3>
    </div>

    <form onSubmit={handleCreateOffer} className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">

      <div>
        <label className="block text-xs text-gray-500 mb-1">Title</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., Festive Gigs Booster"
          required
        />
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Total Earnings</label>
        <input
          value={totalEarnings}
          onChange={(e) => setTotalEarnings(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., 1500"
          required
        />
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Validity</label>
        <input
          value={validity}
          onChange={(e) => setValidity(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., 2025-12-31"
          required
        />
      </div>

      <div className="md:col-span-3">
        <button
          type="submit"
          className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700"
        >
          Create Offer
        </button>
      </div>
    </form>
  </div>

  {/* ===== OFFERS LIST ===== */}
  <div className="bg-white rounded-xl shadow-sm border border-gray-200">
    <div className="flex items-center justify-between px-4 py-3 border-b">
      <h3 className="text-sm font-semibold">All Driver Offers</h3>
    </div>

    <div className="p-2">

      {offers.length === 0 && !loading && (
        <div className="text-sm text-gray-500 px-2 py-3">
          No offers found. Create one above.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">

        {offers.map((o, idx) => {

          const offerId =
            o?.offerId ||
            o?.offerid ||
            o?.pk_offer_id ||
            o?.id ||
            `offer-${idx}`;

          const expanded = expandedOfferId === offerId;

          return (
            <div
              key={`${offerId}-${idx}`}
              className="rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                <div>
                  <div className="text-sm font-semibold">
                    {o?.title || "Untitled Offer"}
                  </div>
                  <div className="text-xs text-gray-500">
                    Offer ID: {offerId}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setExpandedOfferId(expanded ? null : offerId)
                    }
                    className="px-3 py-1.5 rounded-md text-xs bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {expanded ? "Close" : "Add Incentive"}
                  </button>

                  <button
                    onClick={() => handleDeleteOffer(o)}
                    className="px-3 py-1.5 rounded-md text-xs bg-red-600 text-white hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* OFFER DETAILS */}
              <div className="px-4 py-3 text-sm text-gray-600">
                <div>
                  <span className="text-gray-500">Total Earnings:</span>{" "}
                  {o?.totalEarnings || "-"}
                </div>
                <div>
                  <span className="text-gray-500">Validity:</span>{" "}
                  {o?.validity || "-"}
                </div>
              </div>

              {/* INCENTIVE FORM */}
              {expanded && (
                <div className="border-t px-4 py-3 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Gigs Level
                      </label>
                      <input
                        value={gigsLevel}
                        onChange={(e) => setGigsLevel(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., Bronze"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Orders
                      </label>
                      <input
                        value={orders}
                        onChange={(e) => setOrders(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., 25"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Earnings
                      </label>
                      <input
                        value={earnings}
                        onChange={(e) => setEarnings(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., 500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Incentive
                      </label>
                      <input
                        value={incentive}
                        onChange={(e) => setIncentive(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., +5%"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <button
                      onClick={() => handleAddIncentive(o)}
                      className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Save Incentive
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  </div>
</div>
  );
}
